import React, { forwardRef, useEffect, useRef, useState } from 'react';

import { Badge, Group, Loader, Select, SelectProps, Text, useMantineTheme } from '@mantine/core';
import { SelectItem } from '@mantine/core/lib/Select/types';
import { useShallowEffect } from '@mantine/hooks';
import { IconLock } from '@tabler/icons-react';
import usePaymentMethods from 'hooks/paymentMethod/usePaymentMethods';
import { TAP_TO_PAY_VALUE } from 'types/enums';
import { getPaymentMethodName } from 'utils/formatters';

interface IPaymentMethodSelectProps extends Omit<SelectProps, 'data' | 'rightSection'> {
  athleteId?: string;
  allowCash?: boolean;
  allowTapToPay?: boolean;
  useDefaultPayment?: boolean;
  notFoundMessage?: string;
  isLoading?: boolean;
}

interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
  isDefault: boolean;
  label: string;
  isLocked: boolean;
}

const SelectItemComponent = forwardRef<HTMLDivElement, ItemProps>(
  ({ label, isDefault, isLocked, ...others }: ItemProps, ref) => (
    <div ref={ref} {...others}>
      <Group noWrap>
        {isLocked && <IconLock color='red' size='10px' />}
        <Text size='sm'>{label}</Text>
        {isDefault && <Badge color='kiloBlue'>default</Badge>}
      </Group>
    </div>
  ),
);

const PaymentMethodSelect = ({
  athleteId,
  allowCash = false,
  allowTapToPay = false,
  useDefaultPayment = false,
  onChange,
  value,
  notFoundMessage: nothingFoundMessage,
  isLoading: loading,
  ...selectProps
}: IPaymentMethodSelectProps): JSX.Element => {
  const theme = useMantineTheme();

  const [options, setOptions] = useState<SelectItem[]>([]);
  const [defaultPayment, setDefaultPayment] = useState<SelectItem>();
  const { data, isSuccess, isLoading } = usePaymentMethods({
    athleteId: athleteId,
  });

  const previousAthleteId = useRef(athleteId);

  // Only a change of athlete clears the value, so a Cash or Check value survives an athlete with no cards.
  useEffect(() => {
    if (athleteId !== previousAthleteId.current) {
      previousAthleteId.current = athleteId;
      if (onChange) {
        onChange(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteId]);

  useEffect(() => {
    const tempOptions: SelectItem[] = [];
    let hasDefault: boolean = false;

    if (!athleteId || !isSuccess || data.pages[0].data.count === 0) {
      setOptions([]);
      setDefaultPayment(undefined);
    }

    if (isSuccess && data.pages[0].data.count !== 0) {
      const formattedPaymentMethods: SelectItem[] = data.pages
        .map(page => page.data.results)
        .flat()
        .map(paymentMethod => {
          if (paymentMethod.isDefault) {
            hasDefault = true;

            setDefaultPayment({
              ...paymentMethod,
              value: paymentMethod.paymentMethodId,
              label: getPaymentMethodName(paymentMethod),
              isDefault: true,
            });
          }

          return {
            ...paymentMethod,
            value: paymentMethod.paymentMethodId,
            label: getPaymentMethodName(paymentMethod),
            isDefault: paymentMethod.isDefault,
          };
        });

      tempOptions.push(...formattedPaymentMethods);
    }

    if (allowTapToPay) {
      tempOptions.push({
        value: TAP_TO_PAY_VALUE,
        label: TAP_TO_PAY_VALUE,
      });
    }

    if (allowCash) {
      tempOptions.push({
        value: 'Cash or Check',
        label: 'Cash or Check',
      });
      tempOptions.push({
        value: 'Gift Card',
        label: 'Gift Card',
      });
      tempOptions.push({
        value: 'E Transfer',
        label: 'E Transfer',
      });
      tempOptions.push({
        value: 'POS Terminal',
        label: 'POS Terminal',
      });
    }

    if (!hasDefault) {
      setDefaultPayment(undefined);
    }

    setOptions(tempOptions);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSuccess, athleteId, allowTapToPay]);

  useShallowEffect(() => {
    if (useDefaultPayment && onChange && defaultPayment) {
      onChange(defaultPayment.value);
    }
  }, [defaultPayment]);

  return (
    <Select
      data={options}
      onChange={onChange}
      itemComponent={SelectItemComponent}
      nothingFound={nothingFoundMessage || (!athleteId ? 'User is not selected' : 'No payment methods')}
      rightSection={isLoading || loading ? <Loader size={theme.fontSizes.md} /> : undefined}
      value={value}
      icon={
        value && data?.pages[0]?.data?.results.filter(pm => pm.paymentMethodId === value)[0]?.isLocked ? (
          <IconLock color='red' size='10px' />
        ) : null
      }
      {...selectProps}
    />
  );
};

export default PaymentMethodSelect;
